/**
 * useQRCodeList
 *
 * Fetches the paginated list of QR codes for the current portal.
 *
 * Returns:
 *   loading    - true while the request is in flight
 *   error      - Error object if something went wrong, null otherwise
 *   list       - Array of QR code records for the current page
 *   pagination - Raw pagination object from the API { page, limit, total, pages, hasNext, hasPrev }
 *   page       - current page number (1-based)
 *   pageSize   - rows per page
 *   setPage    - (page, pageSize?) => void — change page / page-size
 *   refetch    - re-trigger the current page
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchQRCodeList } from '../api/qrApi';
import { getPortalId } from '../utils/portalId';

const DEFAULT_PAGE_SIZE = 10;

export function useQRCodeList() {
  const [page, setPageState]         = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading]        = useState(true);
  const [error, setError]            = useState(null);
  const [list, setList]              = useState([]);
  const [pagination, setPagination]  = useState({});
  const [tick, setTick]              = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  const setPage = useCallback((newPage, newPageSize) => {
    setPageState(newPage);
    if (newPageSize != null) setPageSizeState(newPageSize);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const portalId = getPortalId();
        const { list: data, pagination: pag } = await fetchQRCodeList({
          portalId,
          page,
          limit: pageSize,
        });
        if (!cancelled) {
          setList(data);
          setPagination(pag);
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, tick]);

  return { loading, error, list, pagination, page, pageSize, setPage, refetch };
}
